import { Icon } from '@iconify/react'
import get from 'lodash/get'
import { useContext, useEffect, useMemo, useState } from 'react'
import { Dropdown } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router'
import { toast } from 'react-toastify'
import { NotificationContext } from 'src/context/NotificationContext'
import {
  Organization,
  OrganizationCategory,
  OrganizationUsagePlan,
  SubscriptionStatus,
  WorkingRole,
  useGetOrganizationQuery,
  useUpdateUserRoleMutation,
} from 'src/gql/graphql'
import { isSuperAdminEmail } from 'src/utils/admin'
import { AuthContext } from '../Auth/AuthProvider'
import CreateCompanyForm from '../IT/CreateCompanyForm'

export default function OrganizationSwitcher() {
  const { userInfo, setUserInfo } = useContext(AuthContext)
  const history = useHistory()
  const [currentOrganization, setCurrentOrganization] = useState<
    Organization | null | undefined
  >(userInfo?.organization)
  const {
    setIsDisabledWorkspace,
    setIsTokenAvailable,
    setIsExceedingPlanWorkspace,
  } = useContext(NotificationContext)

  const [currentOrgId, setCurrentOrgId] = useState<string | null>(
    localStorage.getItem('organizationId'),
  )
  const { t } = useTranslation()
  const isSuperAdmin = isSuperAdminEmail(userInfo?.email as string)

  const [switchProfile, { loading: switchProfileLoading }] =
    useUpdateUserRoleMutation()

  const { data: organizationData } = useGetOrganizationQuery({
    variables: { id: currentOrgId || '' },
    onCompleted: (data) => {
      if (
        data.organization?.subscriptionDetails?.status ===
        SubscriptionStatus.PastDue
      ) {
        setIsDisabledWorkspace(true)
      } else {
        setIsDisabledWorkspace(false)
      }
      // TODO-Cris: Update this logic
      if (
        data.organization?.usagePlan === OrganizationUsagePlan.Explore &&
        data.organization.stripeUpgradePrompt?.id
      ) {
        if (
          data.organization.stripeUpgradePrompt.gracePeriodEnd &&
          new Date(data.organization.stripeUpgradePrompt.gracePeriodEnd)
            .toISOString()
            .split('T')[0] > new Date().toISOString().split('T')[0]
        ) {
          setIsExceedingPlanWorkspace(true)
        } else {
          // if the grace period has ended and they did not upgrade => disable workspace
          setIsDisabledWorkspace(true)
        }
      } else {
        setIsExceedingPlanWorkspace(false)
      }
    },
    onError: () => {
      toast.error(
        t('notification.errorLoading', {
          name: t('common.word.workspace'),
        }),
      )
    },
  })

  const organizationList = useMemo(() => {
    const userOrgs =
      userInfo?.userOrganizations?.map((userOrg) => userOrg.organization) ?? []
    return userOrgs
  }, [userInfo?.userOrganizations])

  //Check if the current organization is personal and the user is the owner
  const isCurrentOrgPersonal = useMemo(
    () =>
      currentOrganization?.category === OrganizationCategory.Personal &&
      currentOrganization.ownerId === userInfo?.id,
    [currentOrganization, userInfo],
  )

  //Check if the user has owned 3 organizations
  const isMaximumOrganizations = useMemo(() => {
    const organizationsThatIsOwner =
      userInfo?.userOrganizations?.filter(
        (userOrg) => userOrg.organization.ownerId === userInfo.id,
      ) ?? []

    return organizationsThatIsOwner.length >= 3
  }, [userInfo])

  useEffect(() => {
    const organisation = get(userInfo, 'organization')
    if (organisation) {
      setCurrentOrgId(organisation.id)
    }
  }, [userInfo])

  useEffect(() => {
    if (!organizationList.length) return

    const localStorageOrg = organizationList.find(
      (org) => org.id === currentOrgId,
    )
    const defaultOrg = userInfo?.organization

    if (!localStorageOrg && !defaultOrg) {
      console.error(
        'Failed to load organizations: No active or stored organization found.',
      )
    }

    setCurrentOrganization(localStorageOrg ?? defaultOrg)
  }, [organizationList, currentOrgId])

  const handleOrganizationChange = (organizationId: string) => {
    localStorage.setItem('organizationId', organizationId)
    // clear history.push state
    history.replace({ state: {} })
    setCurrentOrgId(organizationId)

    const updatedOrganization = organizationList.find(
      (org) => org.id === organizationId,
    )

    let newUserRoleId = ''

    setUserInfo((prev) => {
      if (!prev) return prev

      const updatedRoles = prev.permissions?.find(
        (permission) => permission?.organizationId === organizationId,
      )?.workingRoles as WorkingRole[]

      if (
        updatedRoles.every((role) => role.id !== prev.employeeDetails?.role?.id)
      ) {
        newUserRoleId = updatedRoles[0].id
      }

      return {
        ...prev,
        organization: updatedOrganization,
        roles: updatedRoles,
      }
    })

    if (newUserRoleId && userInfo?.employeeDetails?.employeeId) {
      switchProfile({
        variables: {
          employeeId: userInfo.employeeDetails.employeeId,
          roleId: newUserRoleId,
        },
        onCompleted: () => {
          setIsTokenAvailable(true)
          window.location.reload()
        },
      })
    }
  }

  return (
    <Dropdown>
      <Dropdown.Toggle
        className="nav-link d-flex align-items-center"
        id="dropdown-language"
        variant="link"
        style={{
          color: isCurrentOrgPersonal ? 'var(--color-primary)' : 'white',
          background: isCurrentOrgPersonal ? 'white' : 'transparent',
        }}
      >
        <div
          className={`me-2 border ${isCurrentOrgPersonal ? 'border-primary' : ''} rounded-circle text-center`}
          style={{
            width: 20,
            height: 20,
            fontSize: 13,
          }}
        >
          {currentOrganization?.name[0]}
        </div>

        <span
          className="fs-6 d-inline-block"
          style={{
            color: isCurrentOrgPersonal ? 'var(--color-primary)' : 'white',
          }}
        >
          {currentOrganization?.name}
        </span>
      </Dropdown.Toggle>

      <Dropdown.Menu className="p-1">
        {organizationList.map((organization) => {
          const { id, name } = organization

          return (
            <Dropdown.Item
              key={id}
              className="rounded"
              style={{
                ...(id === currentOrgId && {
                  background: 'var(--primary-color)',
                  color: 'var(--color-on-primary)',
                }),
              }}
              onClick={() => id && handleOrganizationChange(id)}
            >
              {name}
            </Dropdown.Item>
          )
        })}
        {
          <div className="my-1">
            <CreateCompanyForm
              btnProps={{
                style: {
                  width: '100%',
                },
                label: (
                  <span className="d-flex gap-1">
                    <Icon height="20" icon="ix:plus" width="20" />
                    {t('common.word.addWorkspace', 'Add a Workspace')}
                  </span>
                ),
                disabled: isSuperAdmin ? false : isMaximumOrganizations,
              }}
            />
          </div>
        }
      </Dropdown.Menu>
    </Dropdown>
  )
}
